// מסך 3 — קמפיין: בריף, טקסטים נעולים, חריגות מהמותג, עדכון גרסת מותג עם diff, חומרים וסטים.
import { useState } from 'react';
import Icon from '../components/Icon';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, Loading, fmtDate, useLoad, useMe } from '../components/ui';
import { CAMPAIGN_STATUS, DiffList, KIT_FIELD_LABELS, PageHead, Tabs, confirmAction, useAction } from '../components/mgmt/common';
import { BriefForm, CampaignMaterials, CopyEditor, OverridesEditor, type CampaignFull } from '../components/mgmt/CampaignParts';

type Tab = 'brief' | 'copy' | 'look' | 'materials' | 'sets';

export default function CampaignPage() {
  const { id } = useParams();
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get('tab') as Tab) || 'sets';
  const setTab = (t: Tab) => setSp((p) => { p.set('tab', t); return p; }, { replace: true });
  const { data, error, loading, setData, reload } = useLoad<CampaignFull>(`/api/campaigns/${id}`);
  const client = useLoad<{ client: { id: string; name: string } }>(data ? `/api/clients/${data.campaign.client_id}` : null);

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBox error={error} />;
  if (!data) return null;
  const c = data.campaign;
  const clientName = client.data?.client.name ?? 'לקוח';

  return (
    <div>
      <PageHead title={c.name}
        crumbs={<><Link to="/clients">לקוחות</Link> / <Link to={`/clients/${c.client_id}?tab=campaigns`}>{clientName}</Link> / {c.name}</>}
        sub={<><span className="mono">{c.campaign_key}</span> · {CAMPAIGN_STATUS[c.status] ?? c.status} · עודכן {fmtDate(c.updated_at)}</>}>
        <span className="badge info">{data.usesLocalKit ? 'שפה גרפית מקומית' : `מותג גרסה ${data.brandVersion.version}`}</span>
        {data.overridden.length > 0 && <span className="badge warn">חריגה מהמותג: {data.overridden.map((k) => KIT_FIELD_LABELS[k] ?? k).join(', ')}</span>}
        <Link className="btn" to={`/campaigns/${c.id}/links`}>קישורים מתויגים</Link>
        <Link className="btn" to="/exports">בדיקות ויצוא</Link>
        <Link className="btn primary" to={`/demo?campaign=${c.id}&run=1`}>בדיקה באתר דמו</Link>
        {canEdit && <Link className="btn" to={`/brand-import?clientId=${c.client_id}&campaignId=${c.id}`}>שפה מקומית מחומרים</Link>}
      </PageHead>

      {data.newerBrandVersion && <BrandUpgrade campaignId={c.id} canEdit={canEdit} onUpgraded={setData} />}

      <Tabs<Tab> label="אזורי קמפיין" value={tab} onChange={setTab} tabs={[
        { id: 'sets', label: `סטים (${data.sets.length})` },
        { id: 'brief', label: 'בריף' },
        { id: 'copy', label: 'טקסטים' },
        { id: 'look', label: <>שפה גרפית{data.overridden.length > 0 && ' ⚠'}</> },
        { id: 'materials', label: `חומרים (${data.assets.length})` },
      ]} />
      <div role="tabpanel" className="card">
        {tab === 'brief' && <BriefForm key={c.updated_at} c={c} canEdit={canEdit} onSaved={setData} />}
        {tab === 'copy' && <CopyEditor key={c.updated_at} c={c} canEdit={canEdit} onSaved={setData} />}
        {tab === 'look' && <OverridesEditor key={c.updated_at + data.brandVersion.id} data={data} canEdit={canEdit} onSaved={setData} />}
        {tab === 'materials' && <CampaignMaterials c={c} canEdit={canEdit} onSaved={setData} />}
        {tab === 'sets' && (
          <div className="stack">
            <div className="row between">
              <span className="small muted">סט מכיל וריאציות לפי אתר, מיקום וגודל. כל גודל מקבל קומפוזיציה נפרדת.</span>
              <div className="row">
                <button className="btn" onClick={reload}>רענון</button>
                {canEdit && <Link className="btn primary" to={`/new?add=${c.id}`}>סט חדש</Link>}
              </div>
            </div>
            {data.sets.length === 0 ? (
              <div className="empty-cta">
                <Icon name="image" size={36} />
                <strong>עדיין אין באנרים בקמפיין הזה</strong>
                <span>סט הוא קבוצת באנרים של הקמפיין לכל הגדלים. יוצרים אותו בכמה לחיצות.</span>
                {canEdit && <Link className="btn primary big" to={`/new?add=${c.id}`}><Icon name="plus" size={20} />יצירת סט באנרים</Link>}
              </div>
            ) : (
              <table className="table cards-sm">
                <thead><tr><th>סט</th><th>וריאציות</th><th>סטטוס</th><th>עודכן</th><th></th></tr></thead>
                <tbody>{data.sets.map((s) => (
                  <tr key={s.id}>
                    <td><Link to={`/sets/${s.id}`}><strong>{s.name}</strong></Link></td>
                    <td data-l="וריאציות">{s.variant_count}</td>
                    <td data-l="סטטוס"><span className="badge">{s.status}</span></td>
                    <td data-l="עודכן" className="small muted">{fmtDate(s.updated_at)}</td>
                    <td><Link className="btn small" to={`/exports?setId=${s.id}`}>יצוא</Link></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BrandUpgrade({ campaignId, canEdit, onUpgraded }: { campaignId: string; canEdit: boolean; onUpgraded: (r: CampaignFull) => void }) {
  const [diff, setDiff] = useState<{ fromVersion: number; toVersion: number; toVersionId: string; changes: { field: string; before: unknown; after: unknown }[] } | null>(null);
  const load = useAction();
  const up = useAction();
  const show = async () => { const r = await load.run(() => api.get(`/api/campaigns/${campaignId}/brand-diff`)); if (r) setDiff(r); };
  const upgrade = async () => {
    if (!diff) return;
    if (!confirmAction(`לעדכן את הקמפיין לגרסת מותג ${diff.toVersion}? באנרים קיימים לא ישתנו אוטומטית; באנרים חדשים ישתמשו בגרסה החדשה.`)) return;
    const r = await up.run(() => api.post<CampaignFull>(`/api/campaigns/${campaignId}/upgrade-brand`, { brandVersionId: diff.toVersionId }), `הקמפיין עודכן לגרסה ${diff.toVersion}`);
    if (r) { setDiff(null); onUpgraded(r); }
  };
  return (
    <div className="alert warn" style={{ marginBottom: 12 }} role="region" aria-label="גרסת מותג חדשה">
      <div className="row between">
        <span><strong>קיימת גרסת מותג חדשה יותר.</strong> הקמפיין נשאר על הגרסה הנוכחית עד לעדכון מפורש. באנרים קיימים אינם משתנים.</span>
        {!diff && <button className="btn small" onClick={show} disabled={load.busy}>{load.busy ? 'טוען…' : 'הצגת הבדלים'}</button>}
      </div>
      <ErrorBox error={load.error} />
      {diff && (
        <div style={{ marginTop: 8, background: 'var(--surface)', color: 'var(--text)', padding: 10, borderRadius: 6 }}>
          <h3 style={{ marginTop: 0 }}>גרסה {diff.fromVersion} ← גרסה {diff.toVersion}</h3>
          <DiffList changes={diff.changes} labels={KIT_FIELD_LABELS} />
          <ErrorBox error={up.error} />
          <div className="row" style={{ marginTop: 8 }}>
            {canEdit && <button className="btn primary" onClick={upgrade} disabled={up.busy}>{up.busy ? 'מעדכן…' : `עדכון הקמפיין לגרסה ${diff.toVersion}`}</button>}
            <button className="btn" onClick={() => setDiff(null)}>סגירה</button>
          </div>
        </div>
      )}
    </div>
  );
}
